<template>
  <div v-if="componentState != emailPageStates.hideEmail">
    <!--header-->
    <section>
      <h1 class="opposite-ends">
        <span class="h1">{{ t("Common.email") }}</span>
        <button
          v-if="displayEditButton"
          id="editEmail"
          type="button"
          class="text-sm inline-btn-link no-shrink no-print"
          @click="editEmail()"
        >
          {{ t("Common.edit") }}
        </button>
      </h1>
    </section>
    <!--content-->
    <section class="constrain-65">
      <!--initial view-->
      <section v-if="componentState == emailPageStates.enterEmail">
        <p>{{ tn("enterYourEmail") }}</p>
        <BuyEnterEmail @next="emitResponse()" @consumer="nextPage" />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="mb-double" v-html="t('BuyEmailComponent.willSendEmails')"></p>
      </section>
      <!--existing email, not logged in-->
      <section v-if="componentState == emailPageStates.alreadyAnAccount">
        <div class="accent-box-info">
          {{ tn("alreadyAnAccount") }}
          <strong id="existingEmail">{{ userStore.email }}</strong>
        </div>
        <div class="card pa mb">
          <h4 class="mb">
            {{ tn("option1Heading") }}
          </h4>
          <p>
            {{ tn("option1Message") }}
          </p>
          <div class="mb">
            <button type="button" class="btn-outline-primary mr" @click="continueWithoutLoggingIn()">
              {{ tn("btnContinueWithoutLoggingIn") }}
            </button>
            <button type="button" class="btn-primary" @click="logIn()">{{ t("Common.logIn") }}</button>
          </div>
        </div>
        <p class="text-center-sm">{{ tn("or") }}</p>
        <div class="card pa mb">
          <h4 class="mb">
            {{ tn("option2Heading") }}
          </h4>
          <BuyEnterEmail :hide-email="true" @next="emitResponse()" @consumer="nextPage" />
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="mb-double" v-html="t('BuyEmailComponent.willSendEmails')"></p>
      </section>
      <!--email successfully entered-->
      <section v-if="componentState == emailPageStates.addedEmail">
        <p id="addedEmail" class="truncate">
          {{ userStore.currentUser?.email ?? userStore.email }}
        </p>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
//Components
import BuyEnterEmail from "./EnterEmail.vue";
//Stores
import { useUserStore } from "@/stores/user";
//Others
import { ref, watch, onBeforeMount, computed } from "vue";
import { t } from "@/i18n";
import { logEvent } from "@/common/logger";
import { changePageState } from "@/components/Buy/BuyHelper";
import { emailPageStates, pageStates } from "@/components/Buy/BuyEnums";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { getRedirectToLoginURLWithEmail } from "@/common/loginRedirect";
import { pageStates as buyFlowPageStates } from "@/components/Buy/BuyEnums";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { clearSession } from "@/common/logout";
import { unifiedApi } from "@/common";
import { AxiosError } from "axios";
import { useRoute } from "vue-router";
import { useEmailPageStore } from "@/stores/emailPage";

const componentName = "BuyEmailComponent";
logEvent("created", componentName);
const userStore = useUserStore();
//temporary page switching mechanism
const componentState = ref<emailPageStates>(emailPageStates.enterEmail);
const buyFlowStore = useBuyFlowStore();
const editEmailClicked = ref<boolean>(false);
const route = useRoute();
const emailPageStore = useEmailPageStore();

const deviceId = parseInt(route.query?.deviceId?.toString() || route.query?.did?.toString() || "0");

onBeforeMount(() => {
  if (
    (userStore.currentUser || (!userStore.currentUser && buyFlowStore.userGuid)) &&
    buyFlowStore.pageState === buyFlowPageStates.email
  ) {
    changePageState(buyFlowPageStates.payment);
  }
  calculateRendering();
});

const isNewUser = computed(() => userStore.currentUser && !userStore.currentUser?.subscriberInfo?.hasPasswordSet);

const displayEditButton = computed(
  () => (isNewUser.value || !userStore.currentUser) && componentState.value == emailPageStates.addedEmail
);

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

function changeEmailState(mode) {
  componentState.value = mode;
}

async function continueWithoutLoggingIn() {
  if (userStore.currentUser) {
    try {
      await unifiedApi.silentLogout();
    } catch (error) {
      logEvent(`Error on logout: ${error as AxiosError}`, componentName);
    }
    const email = userStore.email;
    await clearSession();
    userStore.email = email;
  }

  changeEmailState(emailPageStates.addedEmail);

  if (buyFlowStore.cart) {
    buyFlowStore.cart.userEmail = userStore.email;
  }

  changePageState(pageStates.payment);
}

function logIn() {
  window.location.href = getRedirectToLoginURLWithEmail(buyFlowStore.prepareReturnUrl("buy"), userStore.email);
}

async function emitResponse() {
  //Move to next state
  await reloadSubscriberInfo();
  changeEmailState(emailPageStates.addedEmail);
  changePageState(pageStates.payment);
}

function editEmail() {
  emailPageStore.reset();
  changePageState(pageStates.email);
  editEmailClicked.value = true;
}

function calculateRendering() {
  let state = emailPageStates.enterEmail;

  if (userStore.preliminaryUserTimedOut) {
    userStore.preliminaryUserTimedOut = false;
    changeEmailState(state);
    return;
  }

  // Edit link was clicked display enterEmail state
  // The (edit link only shows for non-established accounts or established accounts that's not logged in and have no UserGuid)
  if (editEmailClicked.value) {
    editEmailClicked.value = false;
    changeEmailState(state);
    return;
  }

  if (!isNewUser.value) {
    if (userStore.currentUser) {
      state = emailPageStates.addedEmail;
    } else if (userStore.email) {
      state = emailPageStates.alreadyAnAccount;
    }
  }
  if (buyFlowStore.pageState != buyFlowPageStates.email) {
    state = emailPageStates.addedEmail;
    if ((buyFlowStore.userGuid || deviceId > 0) && !userStore.currentUser) {
      state = emailPageStates.hideEmail;
    }
  }
  changeEmailState(state);
}

async function nextPage(isAlreadyInUse: boolean) {
  if (isNewUser.value && !isAlreadyInUse) {
    await emitResponse();
    return;
  }
  changeEmailState(emailPageStates.alreadyAnAccount);
}

watch(
  () => buyFlowStore.triggerStateChange,
  async () => {
    calculateRendering();
  }
);
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
